import { Card, CardContent } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckSquare, Upload } from "react-feather";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  addAndUpload,
  deleteDocumentList,
  getDocumentByOpeAndRef,
  updateAndUpload,
} from "../../../api/documentUpload";
import {
  confirmCharges,
  deleteChargeApprovals,
  getChargeApproveHeader,
  getChargesByChhIdAndChargeCategoryForApproval,
  updateChargeApproval,
} from "../../../api/finance/chargeConfirmation";
import { getBulkDropdownData, getDropdownData } from "../../../api/keyvalue";
import { FORMS, localStorageKeys, MODULES } from "../../../const";
import useUserCompany from "../../../hooks/useUserCompany";
import DocumentUpload from "../../DocumentUpload";
import Grid from "../../Grid";
import Button from "../../input/Button";
import CustomAlert from "../../input/CustomAlert";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";
import {
  DateTimeCellRenderer,
  EVInvCellStyle,
  ResizableScrollingTextRenderer,
  StatusLabelStyle,
} from "../../renderer/StyleRenderer";
import Tab from "../../Tab";
import ChargeList from "./CustomerTransporterChargeApproval/ChargeList";

const modalConst = {
  APPROVE: "approve",
  REJECT: "reject",
  CHARGES: "charges",
};
const HEADER_FROM = FORMS.CHARGE_APPROVAL.HEADER.form;
const DETAIL_FORM = FORMS.CHARGE_APPROVAL.DETAILS.form;
const DOCUMENT_FORM = FORMS.CHARGE_APPROVAL.DOCUMENT.form;

const RemarkPopUp = ({
  key,
  type,
  editMode,
  submitHandler,
  modelRef,
  currentBatchId,
  documents,
  isBatchSearch,
}) => {
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);

  const chargeApprovalDetailPermissions = auth[DETAIL_FORM];
  const chargeApprovalDocumentPermissions = auth[DOCUMENT_FORM];
  const remarkPermissions = chargeApprovalDetailPermissions?.FORM?.REMARK;
  const viewDocumentPermission = chargeApprovalDocumentPermissions?.FORM?.VIEW;
  const [reasonOptions, setReasonOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);

  const [remark, setRemark] = useState("");
  const [reason, setReason] = useState(null);
  const [currentErrors, setCurrentErrors] = useState({});
  const [tab, setTab] = useState(1);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [fileNameOptions, setFileNameOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const docRef = useRef();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const reasonData = await getDropdownData("runnung_status");
      setReasonOptions(reasonData);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!isBatchSearch) {
        setIsDropdownLoading(true);
        const docTypeData = await getDropdownData("doc_type");
        setDocumentTypeOptions(docTypeData);

        const fileNameData = await getDropdownData("file_name", "10");
        setFileNameOptions(fileNameData);

        const statusData = await getDropdownData("status");
        setStatusOptions(statusData);

        const activeData = await getDropdownData("active");
        setActiveOptions(activeData);
        setIsDropdownLoading(false);
      }
    })();
  }, [isBatchSearch]);

  return (
    <div key={key}>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="ui form">
          <div className="ui tabular menu top attached">
            {remarkPermissions && (
              <Tab
                Icon={CheckSquare}
                onClick={() => {
                  setTab(1);
                }}
                active={tab === 1}
                label={type === 1 ? "Approve" : "Reject"}
              />
            )}

            {!isBatchSearch && viewDocumentPermission && (
              <Tab
                Icon={Upload}
                onClick={() => {
                  setTab(2);
                }}
                active={tab === 2}
                label="Document Upload"
              />
            )}
          </div>

          {remarkPermissions && (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none", height: "68vh" }}
            >
              <div className={type === 1 ? "field" : "two fields"}>
                <Input
                  required={type === 2}
                  label="Remark"
                  placeholder="Remark"
                  error={currentErrors.remark}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { remark: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  value={remark}
                  onChange={(e) => {
                    setRemark(e.target.value);
                  }}
                />
                {type === 2 && (
                  <Dropdown
                    required={true}
                    label="Reason"
                    error={currentErrors.reason}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { reason: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Reason"
                    options={reasonOptions}
                    style={{ height: "20px" }}
                    value={reason}
                    onChange={(e, data) => {
                      setReason(data.value);
                    }}
                  />
                )}
              </div>
            </div>
          )}
          {!isBatchSearch && viewDocumentPermission && (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              <DocumentUpload
                module={MODULES.Finance}
                opeType={10}
                ref={docRef}
                reference={currentBatchId}
                dropdown={{
                  documentTypeOptions,
                  statusOptions,
                  fileNameOptions,
                  activeOptions,
                }}
                isLoading={isDropdownLoading}
                editMode={editMode}
                currentValues={documents}
              />
            </div>
          )}
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="button"
              template="create"
              onClick={(e) => {
                e.preventDefault();
                if (type === 2) {
                  let hasError = false;
                  if (!remark) {
                    setCurrentErrors((prevState) => ({
                      ...prevState,
                      remark: "Remark is mandatory when rejecting charges",
                    }));
                    hasError = true;
                  }
                  if (!reason) {
                    setCurrentErrors((prevState) => ({
                      ...prevState,
                      reason: "Reason is mandatory when rejecting charges",
                    }));
                    hasError = true;
                  }
                  if (hasError) {
                    return;
                  }
                }

                if (!isBatchSearch) {
                  const documents = docRef?.current?.getData();
                  submitHandler(
                    type,
                    {
                      reason: reason ? reason : null,
                      remark: remark ? remark : null,
                    },
                    documents,
                  );
                } else {
                  submitHandler(type, {
                    reason: reason ? reason : null,
                    remark: remark ? remark : null,
                  });
                }

                modelRef.current.close();
              }}
              isLoading={false}
              create={!editMode}
              editMode={editMode}
              authenticated={true}
              form={FORMS.CHARGE_APPROVAL.HEADER.form}
              module={MODULES.Finance}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerTransporterChargeApproval = ({
  isBatchSearch,
  type,
  charges,
  batchId,
  isEditMode = false,
  shouldReload = true,
}) => {
  const username = useSelector((state) => state.user.username);
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);

  const userCompany = useUserCompany();

  const [company, setCompany] = useState(
    userCompany ? userCompany.comId : null,
  );

  useEffect(() => {
    setCompany(userCompany ? userCompany.comId : null);
  }, [userCompany]);

  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;

  const chargeApprovalHeaderPermissions = auth[HEADER_FROM];
  const chargeApprovalDetailPermissions = auth[DETAIL_FORM];
  const headerViewPermission = chargeApprovalHeaderPermissions?.FORM?.VIEW;
  const evApprovePermissions =
    chargeApprovalDetailPermissions?.FORM?.EV_APPROVE;
  const invApprovePermissions =
    chargeApprovalDetailPermissions?.FORM?.INV_APPROVE;
  const evRejectPermissions = chargeApprovalDetailPermissions?.FORM?.EV_REJECT;
  const invRejectPermissions =
    chargeApprovalDetailPermissions?.FORM?.INV_REJECT;
  const chargeListPermission =
    chargeApprovalDetailPermissions?.FORM?.CHARGE_LIST;
  const downloadPermission = chargeApprovalDetailPermissions?.FORM?.DOWNLOAD;
  const chargeApprovalDocumentPermissions = auth[DOCUMENT_FORM];
  const viewDocumentPermission = chargeApprovalDocumentPermissions?.FORM?.VIEW;
  const deletePermission = chargeApprovalHeaderPermissions?.FORM?.DELETE;

  const gridRef = useRef();
  const modalRef = useRef();
  const docRef = useRef();

  // State
  const [tab, setTab] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [chargeHeaders, setChargeHeaders] = useState([]);
  const [currentCurrency, setCurrentCurrency] = useState(userCurrencyCode);
  const [currentCurrencyId, setCurrentCurrencyId] = useState(null);
  const [currentChargeDetails, setCurrentChargeDetails] = useState([]);
  const [total, setTotal] = useState("");
  const [batch, setBatchCode] = useState("N/A");
  const [editMode, setEditMode] = useState(isEditMode);
  const [currentBatchId, setCurrentBatchId] = useState(batchId);
  const [documents, setDocuments] = useState([]);
  const [reload, setReload] = useState(shouldReload);
  const [alertMessage, setAlertMessage] = useState("");
  const [uncheckedList, setUncheckedList] = useState([]);
  const [deleteRequest, setDeleteRequest] = useState([]);
  const [selectedChhIds, setSelectedChhIds] = useState([]);
  const [canEvDeletion, setCanEvDeletion] = useState(false);
  const [canInvDeletion, setCanInvDeletion] = useState(false);

  const chargeViewRef = useRef({});
  const unCheckedProceedRef = useRef();
  const deleteProceedRef = useRef();

  useEffect(() => {
    setCurrentCurrency(userCurrencyCode);
  }, [userCurrencyCode]);

  // Loading State
  const [isChargeDataLoading, setIsChargeDataLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Modal
  const [popUpContent, setPopUpContent] = useState(null);
  const [popUpKey, setPopUpKey] = useState(1);

  // Dropdown
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [fileNameOptions, setFileNameOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);

  const mapData = async (
    chargeInfo,
    chg,
    jruId,
    jruCode,
    jruChargeApprovalHeaderId,
    jruCompanyComments,
    jruTransporterComments,
    jruReasonValue,
  ) => {
    setCurrentCurrencyId(chargeInfo.jobChargeHeader.chhCurrency);
    const currencyCode = chargeInfo.jobChargeHeader.chhCurrencyCode;
    const formatter = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    });

    const fmt = (v) => (v != null ? formatter.format(v) : null);

    const currentChargeId = chargeInfo?.jobChargeHeader?.chhId;
    const chargesViewed =
      localStorage.getItem(localStorageKeys.CHARGE_LIST_VIEW) ?? null;

    const chargeViewObj = chargesViewed ? JSON.parse(chargesViewed) : null;
    chargeViewRef.current = chargeViewObj;

    return {
      jobId: chg?.jobId,
      jruId: jruId,
      jruCode,
      jruChargeApprovalHeaderId,
      job: chg?.jobCode,
      reqCode: chg?.requestCode,
      vehicleId: chg?.vehId,
      vehicleType: chg?.vehicleType,
      vehicle: chg?.vehNo,
      transporter: chg?.tranId,
      customer: chg?.cusId,
      chhId: currentChargeId,
      chhCode: chargeInfo?.jobChargeHeader?.chhCode,
      chhDate: chargeInfo?.jobChargeHeader?.chhFormDate,
      startLocation: chg?.startLocation,
      endLocation: chg?.endLocation,
      viaLocation: chg?.viaLocation,
      km: chargeInfo?.jobChargeHeader?.chhKm,
      otherRequests: chg?.otherRequests,
      currencyCode: currencyCode,
      unFormattedTotal:
        type === 1
          ? chargeInfo?.jobChargeHeader?.chhRevenue
          : chargeInfo?.jobChargeHeader?.chhCost,
      total:
        type === 1
          ? formatter.format(chargeInfo?.jobChargeHeader?.chhRevenue)
          : formatter.format(chargeInfo?.jobChargeHeader?.chhCost),
      status:
        type === 1
          ? chargeInfo?.jobChargeHeader?.chhInvStatusValue
          : chargeInfo?.jobChargeHeader?.chhEvStatusValue,
      active: chargeInfo?.jobChargeHeader?.chhActiveValue,
      chargeDetails: chargeInfo?.jobChargeDetails,
      editMode:
        type === 1
          ? chargeInfo?.jobChargeHeader?.chhInvStatus !== 1
          : chargeInfo?.jobChargeHeader?.chhEvStatus !== 1,
      chargeListViewed:
        chargeViewObj && currentChargeId
          ? chargeViewObj[`${currentChargeId}_${type}`]
          : false,
      remark: type === 1 ? jruCompanyComments : jruTransporterComments,
      rejectReason:
        (type === 1 && chargeInfo?.jobChargeHeader?.chhInvStatus === 4) ||
        (type === 2 && chargeInfo?.jobChargeHeader?.chhEvStatus === 4)
          ? jruReasonValue
          : "N/A",
      createdBy: chargeInfo?.jobChargeHeader?.createdBy,
      createdDate: chargeInfo?.jobChargeHeader?.createdDate,
      customerName: chargeInfo?.jobChargeHeader?.customer,
      shipper: chargeInfo?.jobChargeHeader?.shipper,

      totalCharge: chargeInfo?.jobChargeHeader?.totalCharge,
      transportCharge: chargeInfo?.jobChargeHeader?.transportCharge,
      demurrageCharge: chargeInfo?.jobChargeHeader?.demurrageCharge,
      nightParkCharge: chargeInfo?.jobChargeHeader?.nightParkCharge,
      managementCharge: chargeInfo?.jobChargeHeader?.managementCharge,
      fuelAdjCharge: chargeInfo?.jobChargeHeader?.fuelAdjCharge,
      droppingPointCharge: chargeInfo?.jobChargeHeader?.droppingPointCharge,
      unloadingCharge: chargeInfo?.jobChargeHeader?.unloadingCharge,
      loadUnloadCharge: chargeInfo?.jobChargeHeader?.loadUnloadCharge,
      lashingCharge: chargeInfo?.jobChargeHeader?.lashingCharge,
      highwayCharge: chargeInfo?.jobChargeHeader?.highwayCharge,
      tallyCharge: chargeInfo?.jobChargeHeader?.tallyCharge,
      boiCharge: chargeInfo?.jobChargeHeader?.boiCharge,
      loadingCharge: chargeInfo?.jobChargeHeader?.loadingCharge,
      containerCharge: chargeInfo?.jobChargeHeader?.containerCharge,
      weighingCharge: chargeInfo?.jobChargeHeader?.weighingCharge,
      discountCharge: chargeInfo?.jobChargeHeader?.discountCharge,
      otherCharge: chargeInfo?.jobChargeHeader?.otherCharge,

      arrivedAtPickup: chargeInfo?.jobChargeHeader?.arrivedAtPickup,
      departedFromPickup: chargeInfo?.jobChargeHeader?.departedFromPickup,
      arrivedAtDelivery: chargeInfo?.jobChargeHeader?.arrivedAtDelivery,
      departedFromDelivery: chargeInfo?.jobChargeHeader?.departedFromDelivery,

      billingCustomer: chargeInfo?.jobChargeHeader?.billingCustomer,
      reqArrivalTime: chargeInfo?.jobChargeHeader?.reqArrivalTime,
      reqDeliveryTime: chargeInfo?.jobChargeHeader?.reqDeliveryTime,

      shipmentType: chargeInfo?.jobChargeHeader?.shipmentType,
      consignmentType: chargeInfo?.jobChargeHeader?.consignmentType,
      consignee: chargeInfo?.jobChargeHeader?.consignee,

      actualCbm: chargeInfo?.jobChargeHeader?.actualCbm,
      requestCbm: chargeInfo?.jobChargeHeader?.requestCbm,
      creatorRemark: chargeInfo?.jobChargeHeader?.creatorRemark,

      plannedKm: Math.round(chargeInfo?.jobChargeHeader?.plannedKm ?? 0.0),
      cargoInHand: chargeInfo?.jobChargeHeader?.cargoInHand,

      department: chargeInfo?.jobChargeHeader?.department,
      poNo: chargeInfo?.jobChargeHeader?.poNo,
      currentStatus: chargeInfo?.jobChargeHeader?.currentStatus,
      statusColor: chargeInfo?.jobChargeHeader?.statusColor,

      formattedTotalCharge: fmt(chargeInfo?.jobChargeHeader?.totalCharge),
      formattedTransportCharge: fmt(
        chargeInfo?.jobChargeHeader?.transportCharge,
      ),
      formattedDemurrageCharge: fmt(
        chargeInfo?.jobChargeHeader?.demurrageCharge,
      ),
      formattedNightParkCharge: fmt(
        chargeInfo?.jobChargeHeader?.nightParkCharge,
      ),
      formattedManagementCharge: fmt(
        chargeInfo?.jobChargeHeader?.managementCharge,
      ),
      formattedFuelAdjCharge: fmt(chargeInfo?.jobChargeHeader?.fuelAdjCharge),
      formattedDroppingPointCharge: fmt(
        chargeInfo?.jobChargeHeader?.droppingPointCharge,
      ),
      formattedUnloadingCharge: fmt(
        chargeInfo?.jobChargeHeader?.unloadingCharge,
      ),
      formattedLoadUnloadCharge: fmt(
        chargeInfo?.jobChargeHeader?.loadUnloadCharge,
      ),
      formattedLashingCharge: fmt(chargeInfo?.jobChargeHeader?.lashingCharge),
      formattedHighwayCharge: fmt(chargeInfo?.jobChargeHeader?.highwayCharge),
      formattedTallyCharge: fmt(chargeInfo?.jobChargeHeader?.tallyCharge),
      formattedBoiCharge: fmt(chargeInfo?.jobChargeHeader?.boiCharge),
      formattedLoadingCharge: fmt(chargeInfo?.jobChargeHeader?.loadingCharge),
      formattedContainerCharge: fmt(
        chargeInfo?.jobChargeHeader?.containerCharge,
      ),
      formattedWeighingCharge: fmt(chargeInfo?.jobChargeHeader?.weighingCharge),
      formattedDiscountCharge: fmt(chargeInfo?.jobChargeHeader?.discountCharge),
      formattedOtherCharge: fmt(chargeInfo?.jobChargeHeader?.otherCharge),
    };
  };

  useEffect(() => {
    if (editMode && currentBatchId && reload) {
      (async () => {
        setIsChargeDataLoading(true);

        const chargeApproveHeader =
          await getChargeApproveHeader(currentBatchId);

        const chargeInfoList = [];

        let total = 0;
        for (let {
          jruId,
          jruCode,
          jruChargeApprovalHeaderId,
          chargeInfo,
          job,
          jruCompanyComments,
          jruTransporterComments,
          jruReasonValue,
        } of chargeApproveHeader.jobChargeApprovalDetails) {
          if (type === 1) {
            total = chargeInfo.chhExchangeRate
              ? total +
                chargeInfo.jobChargeHeader.chhRevenue *
                  chargeInfo.chhExchangeRate
              : total + chargeInfo.jobChargeHeader.chhRevenue;
          } else {
            total = chargeInfo.chhExchangeRate
              ? total +
                chargeInfo.jobChargeHeader.chhCost * chargeInfo.chhExchangeRate
              : total + chargeInfo.jobChargeHeader.chhCost;
          }
          const mappedData = await mapData(
            chargeInfo,
            job,
            jruId,
            jruCode,
            jruChargeApprovalHeaderId,
            jruCompanyComments,
            jruTransporterComments,
            jruReasonValue,
          );
          chargeInfoList.push(mappedData);
        }
        const formatter = Intl.NumberFormat("en-US", {
          style: "currency",
          currency: userCurrencyCode,
        });
        setTotal(formatter.format(total));
        setChargeHeaders(chargeInfoList);
        setBatchCode(
          chargeApproveHeader.jobChargeApprovalHeaderResponse.insCode,
        );
        const documents = await getDocumentByOpeAndRef(10, currentBatchId);

        setDocuments(documents);
        setIsChargeDataLoading(false);
        setReload(false);
      })();
    }
  }, [editMode, currentBatchId, reload]);

  console.log("selected rows", selectedRows);
  console.log("Type", type);

  useEffect(() => {
    if (!editMode) {
      (async () => {
        setIsChargeDataLoading(true);
        const chargeInfoList = [];
        let total = 0;
        for (let chg of charges) {
          const chargeInfo =
            await getChargesByChhIdAndChargeCategoryForApproval(
              chg?.chgId,
              type,
            );
          if (type === 1) {
            total = chargeInfo.chhExchangeRate
              ? total +
                chargeInfo.jobChargeHeader.chhRevenue *
                  chargeInfo.chhExchangeRate
              : total + chargeInfo.jobChargeHeader.chhRevenue;
          } else {
            total = chargeInfo.chhExchangeRate
              ? total +
                chargeInfo.jobChargeHeader.chhCost * chargeInfo.chhExchangeRate
              : total + chargeInfo.jobChargeHeader.chhCost;
          }

          const mappedData = await mapData(
            chargeInfo,
            {
              ...chg,
              vehicleType: chargeInfo?.jobChargeHeader?.vhtType,
              startLocation: chargeInfo?.jobChargeHeader?.startLocation,
              endLocation: chargeInfo?.jobChargeHeader?.endLocation,
              viaLocation: chargeInfo?.jobChargeHeader?.viaLocation,
              otherRequests: chargeInfo?.jobChargeHeader?.otherRequests,
            },
            null,
            null,
            null,
            null,
            null,
            null,
          );

          chargeInfoList.push(mappedData);
        }
        const formatter = Intl.NumberFormat("en-US", {
          style: "currency",
          currency: userCurrencyCode,
        });

        setTotal(formatter.format(total));
        setChargeHeaders(chargeInfoList);
        setIsChargeDataLoading(false);
      })();
    }
  }, [charges, editMode, type]);

  const cellDoubleClickHandler = (params) => {
    if (params.colDef.field === "total") {
      if (!chargeListPermission) {
        return;
      }
      setCurrentChargeDetails(params.data.chargeDetails);
      setCurrentCurrency(params.data.currencyCode);

      const modifiedObject = chargeViewRef.current
        ? {
            ...chargeViewRef.current,
            [`${params.data.chhId}_${type}`]: true,
          }
        : { [`${params.data.chhId}_${type}`]: true };

      localStorage.setItem(
        localStorageKeys.CHARGE_LIST_VIEW,
        JSON.stringify(modifiedObject),
      );

      chargeViewRef.current = modifiedObject;

      setChargeHeaders((prevState) =>
        prevState.map((chh) => {
          if (chh.chhId === params.data.chhId) {
            return {
              ...chh,
              chargeListViewed: true,
            };
          }
          return chh;
        }),
      );
      setPopUpKey((prevState) => prevState + 1);
      setPopUpContent(modalConst.CHARGES);
      modalRef.current?.showModal();
    }
  };

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current.getSelectedData();
    setSelectedRows(selectedData);

    if (type === 1) {
      for (let selected of selectedData) {
        if (
          selected?.status === "Approved" ||
          selected?.status === "approved"
        ) {
          setCanInvDeletion(true);
        } else {
          setCanInvDeletion(false);
          break;
        }
      }
    } else if (type === 2) {
      for (let selected of selectedData) {
        if (
          selected?.status === "Approved" ||
          selected?.status === "approved"
        ) {
          setCanEvDeletion(true);
        } else {
          setCanEvDeletion(false);
          break;
        }
      }
    }

    const data = [];
    const chhIds = [];
    for (let selected of selectedData) {
      chhIds.push(selected.chhId);
      data.push({
        chhId: selected.chhId,
        jobId: selected.jobId,
        type: type,
        user: username,
        updateStatus: true,
      });
    }
    setDeleteRequest(data);
    setSelectedChhIds(chhIds);
  }, []);

  const calculateOverallInvoiceStatus = (data) => {
    let currentCharge = null;
    for (let charge of data) {
      if (currentCharge === null) {
        currentCharge = charge.status;
      } else {
        if (currentCharge !== charge.status) {
          return 1;
        }
      }
    }
    return currentCharge;
  };

  const getSubmitData = (submitType, headerData, submitInfo) => {
    const mappedChargeHeaders = chargeHeaders;

    for (let selectedCharge of selectedRows) {
      const updateIndex = mappedChargeHeaders.findIndex(
        (chh) => chh.chhId === selectedCharge.chhId,
      );
      mappedChargeHeaders[updateIndex] = {
        ...selectedCharge,
        status: submitType === 1 ? 3 : 4,
        jruCompanyComments: type === 1 ? submitInfo.remark : null,
        jruTransporterComments: type === 2 ? submitInfo.remark : null,
        jruReason: submitInfo.reason,
      };
    }
    return {
      jobChargeApprovalHeaderRequest: {
        insType: type,
        insTotalKm: headerData.reduce((acc, current) => acc + current.km, 0),
        insUom: null,
        insCurrency: currentCurrencyId,
        insAmount: headerData.reduce(
          (acc, current) => acc + current.unFormattedTotal,
          0,
        ),
        insApprovalStatus: calculateOverallInvoiceStatus(mappedChargeHeaders),
        insHaveUnchecked: uncheckedList.length > 0,
        insStatus: 2,
        insActive: 1,
        insCompany: company,
      },
      jobChargeApprovalDetailRequests: mappedChargeHeaders.map((row) => {
        const currentInUnchecked = uncheckedList.some(
          (unc) => unc.chhId === row.chhId,
        );

        return {
          jruId: row.jruId,
          jruChargeApprovalHeader: row.jruChargeApprovalHeaderId,
          jruJob: row.jobId,
          jruJobDate: row.chhDate,
          jruCustomer: row.customer,
          jruChhId: row.chhId,
          jruTransporter: row.transporter,
          jruVehicle: row.vehicleId,
          jruKm: row.km,
          jruAmount: row.unFormattedTotal,
          jruApprovalStatus: row.status,
          jruCompanyComments: row.jruCompanyComments,
          jruTransporterComments: row.jruTransporterComments,
          jruReason: row.jruReason,
          jruStatus: 2,
          jruActive: 1,
          isChecked: !currentInUnchecked,
        };
      }),
      author: username,
    };
  };

  const submitHandler = async (submitType, submitInfo, documents = []) => {
    // submitType = 1 - Approve Selected
    // submitType = 2 - Reject Selected
    setSubmitLoading(true);
    try {
      const allCheckedChargeIds = selectedRows.map(
        (chh) => `${chh.chhId}_${type}`,
      );
      const data = getSubmitData(submitType, chargeHeaders, submitInfo);
      if (editMode) {
        const submitData = {
          ...data,
          jobChargeApprovalHeaderRequest: {
            ...data.jobChargeApprovalHeaderRequest,
            insId: currentBatchId,
          },
        };

        await updateChargeApproval(submitData);

        //if isBatchSearch is true,then i need to execute this part of code
        if (isBatchSearch) {
          documents = docRef?.current?.getData();
        }

        for (let doc of documents.created) {
          const { file, ...rest } = doc;
          const formData = new FormData();
          formData.append("multipartFile", doc.file);
          formData.append("documentRequest", JSON.stringify(rest));
          formData.append("isImage", rest.docCategory === 1);
          const res = await addAndUpload(formData);
          if (res && res?.responseCode === "2040") {
            toast.success(res?.message, {
              autoClose: 5000,
              containerId: "Main",
            });
          } else {
            toast.error(
              res?.message || `Error adding document ${doc.docName}`,
              {
                autoClose: 5000,
                containerId: "Main",
              },
            );
          }
        }

        for (let doc of documents.modified) {
          const { file, ...rest } = doc;
          const formData = new FormData();
          formData.append("multipartFile", doc.file);
          formData.append("documentRequest", JSON.stringify(rest));
          formData.append("isImage", rest.docCategory === 1);
          const res = await updateAndUpload(formData);
          if (res && res?.responseCode === "2040") {
            toast.success(res?.message, {
              autoClose: 5000,
              containerId: "Main",
            });
          } else {
            toast.error(
              res?.message || `Error updating document ${doc.docName}`,
              {
                autoClose: 5000,
                containerId: "Main",
              },
            );
          }
        }

        if (documents?.deleted.length > 0) {
          await deleteDocumentList({
            docIdList: documents?.deleted,
          });
          toast.success("Documents deleted successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }

        let currentCheckedObj = chargeViewRef.current
          ? { ...chargeViewRef.current }
          : {};

        for (let chgId of allCheckedChargeIds) {
          if (currentCheckedObj && currentCheckedObj[chgId]) {
            delete currentCheckedObj[chgId];
          }
        }

        localStorage.setItem(
          localStorageKeys.CHARGE_LIST_VIEW,
          JSON.stringify(currentCheckedObj),
        );
        setReload(true);
        toast.success("Charge approval updated successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
      } else {
        const response = await confirmCharges(data);

        if (isBatchSearch) {
          documents = docRef?.current?.getData();
        }

        for (let doc of documents) {
          const { file, ...rest } = doc;
          const formData = new FormData();
          formData.append("multipartFile", doc.file);
          formData.append(
            "documentRequest",
            JSON.stringify({
              ...rest,
              docReference: response,
            }),
          );
          formData.append("isImage", rest.docCategory === 1);
          const res = await addAndUpload(formData);
          if (res && res?.responseCode === "2040") {
            toast.success(res?.message, {
              autoClose: 5000,
              containerId: "Main",
            });
          } else {
            toast.error(
              res?.message || `Error adding document ${doc.docName}`,
              {
                autoClose: 5000,
                containerId: "Main",
              },
            );
          }
        }

        let currentCheckedObj = chargeViewRef.current
          ? { ...chargeViewRef.current }
          : {};

        for (let chgId of allCheckedChargeIds) {
          if (currentCheckedObj && currentCheckedObj[chgId]) {
            delete currentCheckedObj[chgId];
          }
        }

        localStorage.setItem(
          localStorageKeys.CHARGE_LIST_VIEW,
          JSON.stringify(currentCheckedObj),
        );
        setCurrentBatchId(response);
        setReload(true);
        setEditMode(true);

        toast.success(
          `Charges ${submitType === 0 ? "Approval Status Saved" : submitType === 1 ? "Approved" : "Rejected"} Successfully!`,
          {
            autoClose: 5000,
            containerId: "Main",
          },
        );
      }
    } catch (e) {
      if (e.response?.status === 423) {
        toast.error(e.response?.data, {
          autoClose: 5000,
          containerId: "Main",
        });
      } else if (e.response?.status === 406) {
        toast.error("All the selected Jobs should be completed", {
          autoClose: 5000,
          containerId: "Main",
        });
      } else {
        toast.error("Error while confirming charges!", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
    }
    setSelectedRows([]);
    setSubmitLoading(false);
  };

  const deleteHandler = async () => {
    try {
      const data = {
        requests: deleteRequest,
        chhIds: selectedChhIds.join(","),
        type: type,
      };
      const res = await deleteChargeApprovals(data);
      if (res.status === 200) {
        setReload(true);
        toast.success("Deleted successfully..!", {
          autoClose: 5000,
          containerId: "Main",
        });
      } else {
        toast.error(`${res.data}`, {
          autoClose: 5000,
          containerId: "Main",
        });
      }
    } catch (e) {
      toast.error("Error while deleting..!", {
        autoClose: 5000,
        containerId: "Main",
      });
    }
  };

  const actionMenuHandler = (data) => {
    if (data.value === 3) {
      setAlertMessage(`Are you sure you want to delete ?`);
      deleteProceedRef.current?.openAlert();
      return;
    }

    const unCheckedCharges = selectedRows
      .filter((chh) => !chh.chargeListViewed)
      .map((chh) => ({
        job: chh.job,
        charge: chh.chhCode,
        chhId: chh.chhId,
      }));

    const hasZeroTotalCharges = selectedRows.some(
      (chh) => chh.unFormattedTotal === 0,
    );

    if (hasZeroTotalCharges) {
      toast.warn("Total cannot be zero", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }

    const uncheckedLength = unCheckedCharges.length;
    setUncheckedList(unCheckedCharges);
    setPopUpKey((prevState) => prevState + 1);
    setPopUpContent(data.value === 1 ? modalConst.APPROVE : modalConst.REJECT);
    if (uncheckedLength > 0) {
      setAlertMessage(`Please check all the charges before ${data.value === 1 ? "Approving" : "Rejecting"} charges!\n 
        ${unCheckedCharges.map((chg) => `${chg.job} (${chg.charge})`)} ${uncheckedLength === 1 ? "is" : "are"} unchecked.`);
      unCheckedProceedRef.current?.openAlert();
      return;
    }

    modalRef.current?.showModal();
  };

  let modalContent = <></>;
  switch (popUpContent) {
    case modalConst.CHARGES: {
      modalContent = (
        <ChargeList
          type={type}
          charges={currentChargeDetails}
          currencyCode={currentCurrency}
        />
      );
      break;
    }
    case modalConst.APPROVE: {
      modalContent = (
        <RemarkPopUp
          key={popUpKey}
          type={1}
          editMode={editMode}
          submitHandler={submitHandler}
          modelRef={modalRef}
          documents={documents}
          currentBatchId={currentBatchId}
          isBatchSearch={isBatchSearch}
        />
      );
      break;
    }
    case modalConst.REJECT: {
      modalContent = (
        <RemarkPopUp
          key={popUpKey}
          type={2}
          editMode={editMode}
          submitHandler={submitHandler}
          modelRef={modalRef}
          documents={documents}
          currentBatchId={currentBatchId}
          isBatchSearch={isBatchSearch}
        />
      );
      break;
    }
    default:
  }

  const isRowSelectable = useCallback((params) => {
    return !!params.data && params.data.status !== 2;
  }, []);

  useEffect(() => {
    (async () => {
      if (isBatchSearch) {
        setIsDropdownLoading(true);
        const {
          status,
          active,
          doc_type: docTypeData,
          "file_name#10": fileNameData,
        } = await getBulkDropdownData([
          { key: "active" },
          { key: "status" },
          { key: "doc_type" },
          { key: "file_name", group: "10" },
        ]);
        setStatusOptions(status);
        setActiveOptions(active);
        setDocumentTypeOptions(docTypeData);
        setFileNameOptions(fileNameData);
        setIsDropdownLoading(false);
      }
    })();
  }, [isBatchSearch]);

  const getActionMenuOpt = () => {
    const opt = [];

    if (type === 1) {
      if (invApprovePermissions) {
        opt.push({
          key: 1,
          text: "Approve Selected",
          value: 1,
        });
      }

      if (invRejectPermissions) {
        opt.push({
          key: 2,
          text: "Reject Selected",
          value: 2,
        });
      }

      if (canInvDeletion && deletePermission) {
        opt.push({
          key: 3,
          text: "Delete",
          value: 3,
        });
      }
    }

    if (type === 2) {
      if (evApprovePermissions) {
        opt.push({
          key: 1,
          text: "Approve Selected",
          value: 1,
        });
      }

      if (evRejectPermissions) {
        opt.push({
          key: 2,
          text: "Reject Selected",
          value: 2,
        });
      }

      if (canEvDeletion && deletePermission) {
        opt.push({
          key: 3,
          text: "Delete",
          value: 3,
        });
      }
    }
    return opt;
  };

  let chargeCellClassRules = useMemo(
    () => ({
      "header-cell": "data.chargeListViewed === true",
    }),
    [],
  );

  const commonConfig = [
    {
      headerName: "Request",
      field: "reqCode",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      cellClassRules: chargeCellClassRules,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      module: MODULES.Finance,
      pinned: "left",
      minWidth: 200,
      width: 200,
      filter: true,
    },
    {
      headerName: "Charge",
      field: "chhCode",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      cellClassRules: chargeCellClassRules,
      module: MODULES.Finance,
      filter: true,
    },
    {
      headerName: "Created Date",
      field: "createdDate",
      cellRenderer: DateTimeCellRenderer,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Created By",
      field: "createdBy",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Requesting Customer",
      field: "customerName",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: () => ({
        pixelsPerSecond: 0,
      }),
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
    {
      headerName: "Shipper",
      field: "shipper",
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: () => ({
        pixelsPerSecond: 0,
      }),
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
    {
      headerName: "Vehicle",
      field: "vehicle",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Vehicle Type",
      field: "vehicleType",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Total",
      field: "total",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Transport Charge",
      field: "formattedTransportCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Demurrage Charge",
      field: "formattedDemurrageCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Night Park Charge",
      field: "formattedNightParkCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Management Charge",
      field: "formattedManagementCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Fuel Adjustment Charge",
      field: "formattedFuelAdjCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Dropping Point Charge",
      field: "formattedDroppingPointCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Unloading Charge",
      field: "formattedUnloadingCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Load/Unload Charge",
      field: "formattedLoadUnloadCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Lashing Charge",
      field: "formattedLashingCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Highway Charge",
      field: "formattedHighwayCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Tally Charge",
      field: "formattedTallyCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "BOI Charge",
      field: "formattedBoiCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Loading Charge",
      field: "formattedLoadingCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Container Charge",
      field: "formattedContainerCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Weighing Charge",
      field: "formattedWeighingCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Discount Charge",
      field: "formattedDiscountCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Other Charge",
      field: "formattedOtherCharge",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Arrived at Pickup",
      field: "arrivedAtPickup",
      cellRenderer: DateTimeCellRenderer,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Departed from Pickup",
      field: "departedFromPickup",
      cellRenderer: DateTimeCellRenderer,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Arrived at Delivery",
      field: "arrivedAtDelivery",
      cellRenderer: DateTimeCellRenderer,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Departed from Delivery",
      field: "departedFromDelivery",
      cellRenderer: DateTimeCellRenderer,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Billing Customer",
      field: "billingCustomer",
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: () => ({
        pixelsPerSecond: 0,
      }),
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
    {
      headerName: "Pickup",
      field: "startLocation",
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: () => ({
        pixelsPerSecond: 0,
      }),
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Requested Arrival Time",
      field: "reqArrivalTime",
      cellRenderer: DateTimeCellRenderer,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Via Locations",
      field: "viaLocation",
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: () => ({
        pixelsPerSecond: 0,
      }),
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },

    {
      headerName: "Delivery",
      field: "endLocation",
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: () => ({
        pixelsPerSecond: 0,
      }),
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Requested Delivery Time",
      field: "reqDeliveryTime",
      cellRenderer: DateTimeCellRenderer,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Shipment Type",
      field: "shipmentType",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
    {
      headerName: "Consignment Type",
      field: "consignmentType",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
    {
      headerName: "Consignee",
      field: "consignee",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
    {
      headerName: "Other Requests",
      field: "otherRequests",
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: () => ({
        pixelsPerSecond: 0,
      }),
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Actual CBM",
      field: "actualCbm",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Requested CBM",
      field: "requestCbm",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Creator Remark",
      field: "creatorRemark",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Planned KM",
      field: "plannedKm",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Cargo in Hand",
      field: "cargoInHand",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Chargeable KM",
      field: "km",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },

    {
      headerName: "Department",
      field: "department",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
    {
      headerName: "PO No",
      field: "poNo",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      filter: true,
    },
  ];

  const createModeConfig = [
    ...commonConfig,
    {
      headerName: type === 1 ? "INV Status" : "EV Status",
      field: "status",
      cellRenderer: EVInvCellStyle,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Current Status",
      field: "currentStatus",
      authenticated: false,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      cellRenderer: StatusLabelStyle,
      cellStyle: (params) => ({
        backgroundColor: params.data.statusColor || "#fff",
      }),
      filter: true,
    },
  ];

  const editModeConfig = [
    ...commonConfig,
    {
      headerName: "Reject Reason",
      field: "rejectReason",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Remark",
      cellRenderer: ResizableScrollingTextRenderer,
      field: "remark",
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: type === 1 ? "INV Status" : "EV Status",
      field: "status",
      cellRenderer: EVInvCellStyle,
      authenticated: true,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
    },
    {
      headerName: "Current Status",
      field: "currentStatus",
      authenticated: false,
      primaryForm: DETAIL_FORM,
      module: MODULES.Finance,
      cellClassRules: chargeCellClassRules,
      cellRenderer: StatusLabelStyle,
      cellStyle: (params) => ({
        backgroundColor: params.data.statusColor || "#fff",
      }),
      filter: true,
    },
  ];

  return (
    <>
      <CustomAlert
        ref={unCheckedProceedRef}
        message={alertMessage}
        onAccept={() => {
          modalRef.current?.showModal();
        }}
      />
      <CustomAlert
        ref={deleteProceedRef}
        message={alertMessage}
        onAccept={() => {
          deleteHandler();
        }}
      />

      <Card style={{ boxShadow: "none", minWidth: "70vw", width: "95vw" }}>
        <CardContent style={{ boxShadow: "none" }}>
          <div className="ui top attached tabular menu">
            {headerViewPermission && (
              <Tab
                Icon={CheckSquare}
                onClick={() => {
                  setTab(1);
                }}
                active={tab === 1}
                label="Approval"
              />
            )}
            {isBatchSearch && viewDocumentPermission && (
              <Tab
                Icon={Upload}
                onClick={() => {
                  setTab(2);
                }}
                active={tab === 2}
                label="Document Upload"
              />
            )}
          </div>
          {headerViewPermission && (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              <Modal ref={modalRef}>{modalContent}</Modal>
              <div className="ui segment">
                <h4 className="ui dividing header">
                  {type === 1 ? "Customer" : "Transporter"} Charge Approval
                </h4>

                {isChargeDataLoading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="ui form">
                    <div className="two fields">
                      <Input
                        label="Batch Code"
                        readOnly={true}
                        name="description"
                        placeholder="Batch Code"
                        value={batch}
                      />
                      <Input
                        label={`Total ${type === 1 ? "Revenue" : "Cost"}`}
                        readOnly={true}
                        name="total"
                        placeholder={`Total ${type === 1 ? "Revenue" : "Cost"}`}
                        value={total}
                      />
                    </div>
                    <Grid
                      ref={gridRef}
                      isRowSelectable={isRowSelectable}
                      heading="Pick the jobs for charge approval"
                      wrapText={false}
                      multiRowSelection={true}
                      showDownloadButton={downloadPermission}
                      downloadParam={""}
                      downloadAction=" Charge Approval Excel Download"
                      downloadOnlySelected={true}
                      onSelectionChanged={handleSelectionChanged}
                      showActionMenu={selectedRows.length > 0}
                      actionMenuOptions={getActionMenuOpt()}
                      handleActionMenuChange={actionMenuHandler}
                      actionMenuLoading={submitLoading}
                      onCellDoubleClicked={cellDoubleClickHandler}
                      module={MODULES.Finance}
                      searchable={true}
                      config={editMode ? editModeConfig : createModeConfig}
                      content={chargeHeaders}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          {isBatchSearch && viewDocumentPermission && (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              <DocumentUpload
                module={MODULES.Finance}
                opeType={10}
                ref={docRef}
                reference={currentBatchId}
                dropdown={{
                  documentTypeOptions,
                  statusOptions,
                  fileNameOptions,
                  activeOptions,
                }}
                isLoading={isDropdownLoading}
                editMode={editMode}
                currentValues={documents}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default CustomerTransporterChargeApproval;
